import { useEffect, useMemo, useState } from 'react';
import type { PeriodPreset, UserPreferences } from '@ozon-ai/user/contracts';
import { api } from '../../api/client';

export function usePeriodPresets() {
	const [presets, setPresets] = useState<PeriodPreset[]>([]);
	const [primaryPresetId, setPrimaryPresetId] = useState<string | undefined>();
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		let cancelled = false;
		setLoading(true);
		void Promise.all([
			api.get<PeriodPreset[]>('/period-presets'),
			api.get<UserPreferences>('/user/preferences'),
		])
			.then(([nextPresets, preferences]) => {
				if (cancelled) return;
				setPresets(nextPresets);
				setPrimaryPresetId(preferences.primaryPeriodPresetId);
			})
			.catch(() => {
				if (!cancelled) {
					setPresets([]);
					setPrimaryPresetId(undefined);
				}
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, []);

	const sortedPresets = useMemo(() => {
		if (!primaryPresetId) return presets;
		const primary = presets.find((preset) => preset.id === primaryPresetId);
		if (!primary) return presets;
		return [primary, ...presets.filter((preset) => preset.id !== primaryPresetId)];
	}, [presets, primaryPresetId]);

	const primaryPreset = useMemo(
		() => sortedPresets.find((preset) => preset.id === primaryPresetId) ?? null,
		[sortedPresets, primaryPresetId],
	);

	return { presets: sortedPresets, primaryPresetId, primaryPreset, loading };
}
